import { useCallback, useEffect, useState } from 'react';

// Простой кэш stale-while-revalidate: при повторном заходе на экран данные
// показываются мгновенно из кэша, а свежие подгружаются в фоне.
const cache = new Map<string, unknown>();

export function invalidate(prefix?: string) {
  if (!prefix) cache.clear();
  else for (const k of cache.keys()) if (k.startsWith(prefix)) cache.delete(k);
}

// pollMs — интервал живого обновления (рефреш, пока вкладка видима). 0 — отключить.
// 60 с вместо 20 с: D1 тарифицируется по ПРОЧИТАННЫМ строкам, а каждый тик перечитывает
// сводку экрана целиком. Свежесть не теряем: при возврате на вкладку и после собственных
// действий данные обновляются мгновенно (refresh вызывается явно).
export function useData<T>(key: string, fetcher: () => Promise<T>, pollMs = 60000) {
  const [data, setData] = useState<T | null>(() => (cache.get(key) as T) ?? null);

  const refresh = useCallback(() => {
    return fetcher()
      .then((d) => {
        cache.set(key, d);
        setData(d);
        return d;
      })
      .catch(() => undefined);
  }, [key]);

  useEffect(() => {
    const c = cache.get(key) as T | undefined;
    setData(c ?? null);
    refresh();
  }, [key, refresh]);

  // Живое обновление: периодический рефреш + мгновенный при возврате на вкладку.
  // Так двое на одном экране видят изменения друг друга без перезагрузки.
  useEffect(() => {
    if (!pollMs) return;
    const tick = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const iv = setInterval(tick, pollMs);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [refresh, pollMs]);

  return { data, refresh, setData };
}
